import { DashboardLayout, BaseLayout } from "@layouts/index";

import { getProperties } from "@services/krent.service";

import { FiltersCard, SearchCard2 } from "@shared/cards";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";

import { PropertyCardList } from "../../components/lists/PropertyCardList";

import { useSearchParams } from "react-router-dom";

import { useSessionStorage } from "../../hooks";
import Pagination from "components/Pagination";

import PopularLocation from "components/PopularLocation";

import { getPageFromSessionStorage } from "@utils/helpers";
import { Breadcrumb } from "react-bootstrap";
import {
  capitalizedWord,
  extractNumber,
  getH1,
  getH2,
  reverseFormatLinkText,
} from "./helper";

import GetMeta from "./GetMeta";
import Faq from "./Faq";
import RadioSearchNav from "@shared/cards/RadioSearchNav";
import {
  AbujaArticles,
  CommercialArticle,
  HouseArticles,
  IkoyiArticles,
  LagosArticles,
  LandArticle,
  LekkiArticles,
  RentApartmentArticle,
  RentArticles,
  SalesApartmentArticle,
  SalesArticles,
  ShortletArticles,
} from "./Articles";

type ParamsType = {
  [key: string]: string;
};

export const PropertiesPage = () => {
  const [user] = useSessionStorage("user", {});
  const [queryParams] = useSearchParams();
  const location: any = useLocation();
  const [currentType, setCurrentType] = useState<string>("shortlet");

  const {
    type,
    location: paramLocation,
    kind,
    bedroom: paramBedrooms,
  }: any = useParams();

  // Map URL param "rent" to the API intent value "long lease"
  const resolveIntent = (t: string) => (t === "rent" ? "long lease" : t);
  const finalType = resolveIntent(type || currentType);

  const handleTypeChange = (newType: string) => {
    const intent = resolveIntent(newType);
    setCurrentType(intent);
    setPage(1);
    fetchProperties(1, intent);
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<any>(parseInt(getPageFromSessionStorage()));
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const limit: any = 21;
  const [data, setData] = useState<any>({
    properties: [],
    current_page: 1,
    page_count: 0,
    total: 0,
    numOfPages: 1,
  });

  // Fetch properties function
  const fetchProperties = (pageNumber = 1, overrideIntent?: string) => {
    setLoading(true);

    const intent = overrideIntent || finalType;

    const params: ParamsType = {};

    if (paramLocation) {
      params["location.city"] = reverseFormatLinkText(
        capitalizedWord(paramLocation),
      );
    }
    if (paramBedrooms) {
      params["bedrooms"] = extractNumber(paramBedrooms);
    }
    if (kind) {
      if (kind === "house") {
        params["kind"] = "houses";
      }
      if (kind === "apartment") {
        params["kind.title"] = "Apartment";
      }
      if (kind === "commercial") {
        params["kind"] = "commercial";
      }
      if (kind === "land") {
        params["kind.title"] = "Land";
      }
    }
    queryParams.forEach((value, key) => {
      params[key] = value;
    });

    // Update the browser's URL with only the page number if it is greater than 1
    let newUrl = `${window.location.pathname}`;
    if (pageNumber > 1) {
      const urlParams = new URLSearchParams();
      urlParams.set("page", pageNumber.toString());
      newUrl += `?${urlParams.toString()}`;
    }

    window.history.pushState(null, "", newUrl);

    let abortController = new AbortController();

    console.log("Fetching properties with intent:", intent);
    console.log("Full params:", {
      ...params,
      intent,
      limit,
      page: pageNumber.toString(),
    });

    getProperties(
      { ...params, intent, limit, page: pageNumber.toString() },
      abortController.signal,
    )
      .then((res: any) => {
        const { page_count, data, current_page, total } = res;
        setData({
          properties: data,
          page_count,
          total,
          current_page,
          numOfPages: Math.ceil(total / limit),
        });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      abortController.abort();
    };
  };

  // if no global search this takes effect
  useEffect(() => {
    // Update the 'intent' state if the query parameter changes
    if (!location.state?.searchResult || location.state === null) {
      fetchProperties(page);
    }
  }, [queryParams, location.pathname, page]);

  useEffect(() => {
    // this set the page back to 1 if a new intent is called
    if (location?.state?.page) {
      setPage(location?.state?.page);
    }
  }, [type, paramLocation, queryParams]);

  return user && user?._id ? (
    <DashboardLayout>
      <GetMeta
        type={finalType}
        bedroom={paramBedrooms}
        kind={kind}
        location={paramLocation}
        total={data?.total}
      />
      <section className="container mt-5 mb-5">
        <h5 className="fw-bolder mb-3 pt-3">List of properties</h5>
        <div className="bg-white col-12   rounded">
          <RadioSearchNav
            type={finalType}
            setPage={setPage}
            onTypeChange={handleTypeChange}
          />
          <hr
            style={{
              borderColor: "#47BBA9",
              borderWidth: "1px",
              opacity: 1,
              marginTop: 0,
            }}
          />
          <SearchCard2
            extraClass="rounded-top-left-0 rounded-top-right-0"
            sizeClass="col-md-4 col-8"
            page={page}
            setPage={setPage}
          />
        </div>

        {filtersLoaded && (
          <Link
            className="btn btn-link fw-bold px-0 text-danger ml-1 no-decoration"
            to={"/explore"}
            onClick={() => setFiltersLoaded(false)}
          >
            Clear Filters
          </Link>
        )}
      </section>
      <section className="container">
        <Breadcrumb className="mt-2">
          <Breadcrumb.Item href="/">Home</Breadcrumb.Item>

          <Breadcrumb.Item
            active={!kind && !paramLocation ? true : false}
            href={`/property-for-${finalType}`}
          >{`Property for ${finalType}`}</Breadcrumb.Item>
          {kind && (
            <Breadcrumb.Item
              active={!paramLocation ? true : false}
              href={`/property-for-${finalType}/${kind}`}
            >{`${kind}`}</Breadcrumb.Item>
          )}
          {paramLocation && (
            <Breadcrumb.Item active>{`${paramLocation}`}</Breadcrumb.Item>
          )}
        </Breadcrumb>
      </section>
      <section className="container">
        {location.state?.searchResult ? (
          <div>
            <i className="fa fa-remove text-danger"></i>
            <Link
              className=" btn-link  px-0 text-danger ml-1 d-inline-block mt-1  no-decoration"
              to={
                window.location.pathname.includes("properties")
                  ? "/explore"
                  : "/properties"
              }
            >
              Clear search
            </Link>
          </div>
        ) : (
          ""
        )}
        {loading ? (
          <p>Loading....</p>
        ) : data?.properties?.length === 0 ? (
          <h4>
            We couldn't find any matches for your search. Please consider trying
            different keywords.
          </h4>
        ) : (
          <div>
            <h1 className="fs-20 mt-3" style={{ lineHeight: "1.5" }}>
              {getH1(finalType, paramLocation, paramBedrooms, kind)}{" "}
            </h1>
            <h2
              className="fs-14 mb-3 p-3  d-none d-md-block"
              style={{
                lineHeight: "2",
                fontWeight: "normal",
                backgroundColor: "#EFF1EF",
                color: "#171D1B",
                borderRadius: "8px",
              }}
            >
              {getH2(
                finalType,
                paramLocation ? paramLocation : undefined,
                data?.total,
                paramBedrooms,
                kind,
              )}
            </h2>
            <PropertyCardList properties={data.properties} loading={loading} />
          </div>
        )}

        <Pagination
          page={page}
          numOfPages={data.numOfPages}
          setPage={setPage}
        />
        {paramLocation === "lagos" ? (
          <LagosArticles />
        ) : paramLocation === "abuja" ? (
          <AbujaArticles />
        ) : paramLocation === "lekki" ? (
          <LekkiArticles />
        ) : paramLocation === "ikoyi" ? (
          <IkoyiArticles />
        ) : kind === "house" && finalType === "sale" ? (
          <HouseArticles />
        ) : kind === "land" ? (
          <LandArticle />
        ) : finalType === "long lease" && kind === "apartment" ? (
          <RentApartmentArticle />
        ) : finalType === "sale" && kind === "apartment" ? (
          <SalesApartmentArticle />
        ) : finalType === "sale" && kind === "commercial" ? (
          <CommercialArticle />
        ) : kind === "house" && finalType === "long lease" ? (
          ""
        ) : finalType === "sale" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <SalesArticles />
        ) : finalType === "sale" && !paramLocation && kind && !paramBedrooms ? (
          <SalesArticles />
        ) : finalType === "long lease" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <RentArticles />
        ) : finalType === "long lease" &&
          !paramLocation &&
          kind &&
          !paramBedrooms ? (
          <RentArticles />
        ) : finalType === "shortlet" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <ShortletArticles />
        ) : (
          ""
        )}
        {(paramLocation || kind || finalType) && !paramBedrooms && (
          <Faq
            total={data?.total}
            properties={data?.properties}
            type={finalType}
            kind={kind}
            location={paramLocation}
          />
        )}
      </section>

      <section className="container py-5 px-0 px-md-3">
        <PopularLocation />
      </section>

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvasExample"
        aria-labelledby="offcanvasExampleLabel"
      >
        <div className="offcanvas-header px-4">
          <h5 className="offcanvas-title fw-bolder" id="offcanvasExampleLabel">
            Filters
          </h5>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>

        <FiltersCard
          page={page}
          limit={limit}
          setPage={setPage}
          setFiltersLoaded={setFiltersLoaded}
        />
      </div>
    </DashboardLayout>
  ) : (
    <BaseLayout setPage={setPage}>
      <GetMeta
        type={finalType}
        bedroom={paramBedrooms}
        kind={kind}
        location={paramLocation}
        total={data?.total}
      />
      <section className="container " style={{ marginTop: "8rem" }}>
        <h5 className="fw-bolder mb-3">Filter Properties</h5>

        <div className="bg-white col-12  rounded">
          <div className="d-lg-none">
            <RadioSearchNav
              type={finalType}
              setPage={setPage}
              onTypeChange={handleTypeChange}
            />
          </div>
          <SearchCard2
            extraClass="rounded-top-left-0 rounded-top-right-0"
            sizeClass=" col-md-4 col-8"
            page={page}
            setPage={setPage}
          />
        </div>
        {filtersLoaded && (
          <NavLink
            to={"/explore/"}
            className="btn btn-link fw-bold px-0 text-danger ml-1"
            onClick={() => setFiltersLoaded(false)}
          >
            Clear Filters
          </NavLink>
        )}
      </section>
      <section className="container">
        <Breadcrumb className="mt-2">
          <Breadcrumb.Item href="/">Home</Breadcrumb.Item>

          <Breadcrumb.Item
            active={!kind && !paramLocation ? true : false}
            href={`/property-for-${finalType}`}
          >{`Property for ${finalType}`}</Breadcrumb.Item>
          {kind && (
            <Breadcrumb.Item
              active={!paramLocation ? true : false}
              href={`/property-for-${finalType}/${kind}`}
            >{`${kind}`}</Breadcrumb.Item>
          )}
          {paramLocation && (
            <Breadcrumb.Item active>{`${paramLocation}`}</Breadcrumb.Item>
          )}
        </Breadcrumb>
      </section>
      <section className="container">
        {location?.state ? (
          <div>
            <i className="fa fa-remove text-danger"></i>
            <Link
              className=" btn-link  px-0 text-danger ml-1 d-inline-block mt-1  no-decoration"
              to={
                window.location.pathname.includes("properties")
                  ? "/explore"
                  : "/properties"
              }
            >
              Clear search
            </Link>
          </div>
        ) : (
          ""
        )}
        {loading ? (
          <p>Loading....</p>
        ) : data?.properties?.length === 0 ? (
          <h4>
            We couldn't find any matches for your search. Please consider trying
            different keywords.
          </h4>
        ) : (
          <div>
            <h1 className="fs-20 mt-3" style={{ lineHeight: "1.5" }}>
              {" "}
              {getH1(finalType, paramLocation, paramBedrooms, kind)}{" "}
            </h1>
            <h2
              className="fs-14 mb-3 p-3 d-none d-md-block"
              style={{
                lineHeight: "2",
                fontWeight: "normal",
                backgroundColor: "#EFF1EF",
                color: "#171D1B",
                borderRadius: "8px",
              }}
            >
              {getH2(
                finalType,
                paramLocation ? paramLocation : undefined,
                data?.total,
                paramBedrooms,
                kind,
              )}
            </h2>
            <PropertyCardList properties={data.properties} loading={loading} />
          </div>
        )}

        <Pagination
          page={page}
          numOfPages={data.numOfPages}
          setPage={setPage}
        />
        {paramLocation === "lagos" ? (
          <LagosArticles />
        ) : paramLocation === "abuja" ? (
          <AbujaArticles />
        ) : paramLocation === "lekki" ? (
          <LekkiArticles />
        ) : paramLocation === "ikoyi" ? (
          <IkoyiArticles />
        ) : kind === "house" && finalType === "sale" ? (
          <HouseArticles />
        ) : kind === "land" ? (
          <LandArticle />
        ) : finalType === "long lease" && kind === "apartment" ? (
          <RentApartmentArticle />
        ) : finalType === "sale" && kind === "apartment" ? (
          <SalesApartmentArticle />
        ) : finalType === "sale" && kind === "commercial" ? (
          <CommercialArticle />
        ) : kind === "house" && finalType === "long lease" ? (
          ""
        ) : finalType === "sale" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <SalesArticles />
        ) : finalType === "sale" && !paramLocation && kind && !paramBedrooms ? (
          <SalesArticles />
        ) : finalType === "long lease" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <RentArticles />
        ) : finalType === "long lease" &&
          !paramLocation &&
          kind &&
          !paramBedrooms ? (
          <RentArticles />
        ) : finalType === "shortlet" &&
          !paramLocation &&
          !kind &&
          !paramBedrooms ? (
          <ShortletArticles />
        ) : (
          ""
        )}
        {(paramLocation || kind || finalType) && !paramBedrooms && (
          <Faq
            total={data?.total}
            properties={data?.properties}
            type={finalType}
            kind={kind}
            location={paramLocation}
          />
        )}
      </section>

      <section className="container py-5 px-0 px-md-3">
        <PopularLocation />
      </section>
    </BaseLayout>
  );
};
